export type ColorScheme = 'light' | 'dark';
export type ColorSchemePreference = 'light' | 'dark' | 'system';

const COLOR_SCHEME_STORAGE_KEY = 'app_color_scheme';
const EMBED_COLOR_SCHEME_SESSION_KEY = 'embed_color_scheme';

export function isColorSchemePreference(
  value: string | null | undefined,
): value is ColorSchemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

export function isEmbedPathname(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'embed';
}

function getSystemColorScheme(
  media: Pick<MediaQueryList, 'matches'> | null = typeof window === 'undefined'
    ? null
    : window.matchMedia('(prefers-color-scheme: dark)'),
): ColorScheme {
  return media?.matches ? 'dark' : 'light';
}

export function resolveColorScheme(options: {
  isEmbed: boolean;
  storedPreference: string | null;
  embedOverride: string | null;
  systemScheme: ColorScheme;
}): ColorScheme {
  const { isEmbed, storedPreference, embedOverride, systemScheme } = options;

  if (isEmbed) {
    if (isColorSchemePreference(embedOverride) && embedOverride !== 'system') {
      return embedOverride;
    }
    return 'light';
  }

  if (
    isColorSchemePreference(storedPreference) &&
    storedPreference !== 'system'
  ) {
    return storedPreference;
  }

  return systemScheme;
}

function readStoredColorSchemePreference(): ColorSchemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const raw = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
  return isColorSchemePreference(raw) ? raw : 'system';
}

const colorSchemeListeners = new Set<() => void>();

let cachedSnapshot: ColorSchemeSnapshot | null = null;

type ColorSchemeSnapshot = {
  preference: ColorSchemePreference;
  embedOverride: ColorSchemePreference | null;
  systemScheme: ColorScheme;
};

function emitColorSchemeChange(): void {
  cachedSnapshot = null;
  colorSchemeListeners.forEach((listener) => listener());
}

export function subscribeColorScheme(listener: () => void): () => void {
  colorSchemeListeners.add(listener);
  return () => {
    colorSchemeListeners.delete(listener);
  };
}

export function getColorSchemeSnapshot(): ColorSchemeSnapshot {
  const next: ColorSchemeSnapshot = {
    preference: readStoredColorSchemePreference(),
    embedOverride: readLiveEmbedColorSchemeOverride(),
    systemScheme: getSystemColorScheme(),
  };
  if (
    cachedSnapshot &&
    cachedSnapshot.preference === next.preference &&
    cachedSnapshot.embedOverride === next.embedOverride &&
    cachedSnapshot.systemScheme === next.systemScheme
  ) {
    return cachedSnapshot;
  }
  cachedSnapshot = next;
  return cachedSnapshot;
}

export function getServerColorSchemeSnapshot(): ColorSchemeSnapshot {
  return {
    preference: 'system',
    embedOverride: null,
    systemScheme: 'light',
  };
}

export function writeStoredColorSchemePreference(
  preference: ColorSchemePreference,
): void {
  if (typeof localStorage === 'undefined') return;
  if (preference === 'system') {
    localStorage.removeItem(COLOR_SCHEME_STORAGE_KEY);
  } else {
    localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, preference);
  }
  emitColorSchemeChange();
}

function readEmbedColorSchemeOverride(): ColorSchemePreference | null {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(EMBED_COLOR_SCHEME_SESSION_KEY);
  return isColorSchemePreference(raw) ? raw : null;
}

function readLiveEmbedColorSchemeOverride(): ColorSchemePreference | null {
  if (typeof window !== 'undefined') {
    const fromUrl = new URLSearchParams(window.location.search).get(
      'colorScheme',
    );
    if (isColorSchemePreference(fromUrl)) return fromUrl;
  }
  return readEmbedColorSchemeOverride();
}

export function writeEmbedColorSchemeOverride(
  preference: ColorSchemePreference | null,
): void {
  if (typeof sessionStorage === 'undefined') return;
  if (!preference || preference === 'system') {
    sessionStorage.removeItem(EMBED_COLOR_SCHEME_SESSION_KEY);
  } else {
    sessionStorage.setItem(EMBED_COLOR_SCHEME_SESSION_KEY, preference);
  }
  emitColorSchemeChange();
}

export function applyDocumentColorScheme(scheme: ColorScheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.colorScheme = scheme;
}

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', emitColorSchemeChange);
}
